import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { EyeOffIcon } from 'lucide-react';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import type { Device } from '../../../types/db';
interface DoorDetectionProps {
  image: string;
  maskHeight: number;
  detection: NonNullable<Device['detection']>;
  onDetected?: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const pct = (v: number) => `${(v * 100).toFixed(2)}%`;

/** Camera crop: a scan passes over the frame, the detection box closes in on the person, then the label locks. */
export function DoorDetection({
  image,
  maskHeight,
  detection,
  onDetected
}: DoorDetectionProps) {
  const reduce = useMotionPreference();
  const [locked, setLocked] = useState(reduce);
  const [x, y, w, h] = detection.box;
  useEffect(() => {
    if (reduce) {
      onDetected?.();
      return;
    }
    const id = window.setTimeout(() => {
      setLocked(true);
      onDetected?.();
    }, 1250);
    return () => window.clearTimeout(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div className="relative aspect-video overflow-hidden rounded-2xl bg-night-900" data-id="element-1274" data-mp="1ncrozczce50j">
      <img src={image} alt={`Camerabeeld van de voordeur: ${detection.label.toLowerCase()} met een pakket`} className="h-full w-full object-cover" data-id="element-1275" data-mp="19cgzoedokyhh" />

      {/* Privacy mask: street and neighbours are blurred on the camera itself */}
      <div className="absolute inset-x-0 top-0 grid place-items-center bg-night-950/80 backdrop-blur-lg" style={{
      height: pct(maskHeight)
    }} data-id="element-1276" data-mp="zlmab1551irt">
        <EyeOffIcon className="h-5 w-5 text-ink-muted" aria-hidden data-id="element-1277" data-mp="qnkd6woiza9t" />
      </div>

      {!reduce && <motion.div aria-hidden className="absolute inset-x-0 h-[2px]" style={{
      backgroundColor: 'rgb(var(--accent))',
      boxShadow: '0 0 14px rgb(var(--accent))'
    }} initial={{
      top: pct(maskHeight),
      opacity: 1
    }} animate={{
      top: '100%',
      opacity: [1, 1, 0]
    }} transition={{
      duration: 0.85,
      ease: [0.65, 0, 0.35, 1]
    }} data-id="element-1278" data-mp="uo477rdsxxrq" />}

      <motion.div aria-hidden className="absolute rounded-lg border-2" style={{
      borderColor: 'rgb(var(--accent))'
    }} initial={reduce ? false : {
      left: '3%',
      top: pct(maskHeight + 0.03),
      width: '94%',
      height: pct(0.94 - maskHeight - 0.03),
      opacity: 0
    }} animate={{
      left: pct(x),
      top: pct(y),
      width: pct(w),
      height: pct(h),
      opacity: 1
    }} transition={{
      duration: 0.45,
      delay: reduce ? 0 : 0.75,
      ease: EASE
    }} data-id="element-1279" data-mp="2ilvb7ns63s9u" />

      <AnimatePresence data-id="element-1280" data-mp="2xai0oibie0es">
        {locked && <motion.span initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} className="absolute -translate-y-full whitespace-nowrap rounded-md px-2 py-0.5 text-[22px] font-semibold text-night-950" style={{
        left: pct(x),
        top: pct(y),
        backgroundColor: 'rgb(var(--accent))'
      }} data-id="element-1281" data-mp="2x9gfvm4oxzjv">
            {detection.label} · {Math.round(detection.confidence * 100)}%
          </motion.span>}
      </AnimatePresence>
    </div>;
}